import { useState, useEffect, useCallback } from 'react';
import { db } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { User, NotificationPreference, COLLECTION_NAMES } from '../types';

export interface UserSettings {
  receiveNotifications: boolean;
  notificationScope: 'all' | 'tagged';
}

export interface PreferencesMap {
  [key: string]: boolean;
}

// All 10 preference types from the screenshots
export const allPreferenceTypes = [
  'rock_created',
  'rock_completed',
  'rock_dropped',
  'issue_created',
  'issue_resolved',
  'action_item_created',
  'action_item_completed',
  'metric_created',
  'metric_off_track',
  'wins_excited_created',
] as const;

export function useNotificationPreferences() {
  const { currentUser } = useAuth();
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const [preferences, setPreferences] = useState<PreferencesMap>({});
  const [loading, setLoading] = useState(true);

  // 1. Fetch all preferences and user settings
  const fetchPreferences = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch User document for global settings
      const userDocRef = doc(db, COLLECTION_NAMES.USERS, currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data() as User;
        setUserSettings({
          receiveNotifications: userData.receiveNotifications ?? true,
          notificationScope: userData.notificationScope ?? 'all',
        });
      } else {
        // Default settings if user document doesn't exist
        setUserSettings({
          receiveNotifications: true,
          notificationScope: 'all',
        });
      }

      // Fetch all preference documents
      const prefQuery = query(
        collection(db, COLLECTION_NAMES.NOTIFICATION_PREFERENCES),
        where('userId', '==', currentUser.uid)
      );
      const prefSnapshot = await getDocs(prefQuery);

      // Create a map for easy UI state management
      const prefMap: PreferencesMap = {};
      allPreferenceTypes.forEach(type => {
        const found = prefSnapshot.docs.find(
          d => d.data().preferenceType === type
        );
        prefMap[type] = found ? found.data().enabled : false; // Default to false
      });
      
      setPreferences(prefMap);

    } catch (error) {
      console.error("Error fetching preferences:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // 2. Save all preferences using a batch write
  const savePreferences = async (
    newSettings: UserSettings,
    newPrefs: PreferencesMap
  ) => {
    if (!currentUser) throw new Error("User not authenticated");

    const batch = writeBatch(db);

    // 2a. Update the User document
    const userDocRef = doc(db, COLLECTION_NAMES.USERS, currentUser.uid);
    batch.update(userDocRef, { 
      receiveNotifications: newSettings.receiveNotifications,
      // notificationScope is not used - keeping it for backward compatibility but not updating it
    });

    // 2b. Query existing preference docs
    const prefQuery = query(
      collection(db, COLLECTION_NAMES.NOTIFICATION_PREFERENCES),
      where('userId', '==', currentUser.uid)
    );
    const prefSnapshot = await getDocs(prefQuery);
    const existingPrefs = prefSnapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
    })) as (NotificationPreference & { id: string })[];

    // 2c. Batch update/set for each preference
    for (const type of allPreferenceTypes) {
      const existingDoc = existingPrefs.find(p => p.preferenceType === type);
      const newEnabledValue = newPrefs[type] ?? false;

      if (existingDoc) {
        // Update existing document
        batch.update(doc(db, COLLECTION_NAMES.NOTIFICATION_PREFERENCES, existingDoc.id), {
          enabled: newEnabledValue,
          updatedAt: serverTimestamp(),
        });
      } else {
        // Create new document
        const newDocRef = doc(collection(db, COLLECTION_NAMES.NOTIFICATION_PREFERENCES));
        batch.set(newDocRef, {
          userId: currentUser.uid,
          preferenceType: type,
          enabled: newEnabledValue,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }

    // 2d. Commit batch
    await batch.commit();
    // Re-fetch to confirm state
    await fetchPreferences();
  };

  return { userSettings, preferences, loading, savePreferences, fetchPreferences };
}

